import * as React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import AddNewFixedDurationTypeModal from 'src/features/reservations/components/modals/AddNewFixedDurationTypeModal';
import DeleteFixedDurationType from 'src/features/reservations/components/modals/DeleteFixedDurationType';
import EditFixedDurationTypeModal from 'src/features/reservations/components/modals/EditFixedDurationTypeModal';
import { fixedDurationReservationTypeTableColumns } from 'src/features/reservations/components/tables/utils/reservationVersionsTableColumns';
import { useDeleteReservationType } from 'src/features/reservations/hooks/useDeleteReservationType';
import { useReservationTypeList } from 'src/features/reservations/hooks/useReservationTypeList';
import { ReservationVersionsProvider } from 'src/features/reservations/providers/ReservationVersionsProvider';
import { RESERVATION_TYPE, type ReservationType } from 'src/features/reservations/types/reservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { generalErrorMessages } from 'src/utils/messages';

const FixedReservationTypes = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();

    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);

    const [selectedType, setSelectedType] = React.useState<ReservationType | undefined>(undefined);
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'valid_from', desc: true }],
    });
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });

    const permissionReservationsSettings = useHasPermissions([PERMISSION.RESERVATIONS_SETTINGS]);

    const {
        data: reservationData,
        refetch,
        isLoading,
        isError,
    } = useReservationTypeList({
        parkingId: parkingId || '',
        reservationType: RESERVATION_TYPE.FIXED_DURATION,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...querySearchOptions,
        config: { enabled: !!parkingId },
    });

    const { mutate: deleteVersion } = useDeleteReservationType({
        onSuccess: (_) => {
            void refetch();
            setSelectedType(undefined);
            toast({
                title: formatMessage(reservationMessages.deleteVersionSuccess),
                description: formatMessage(reservationMessages.deleteVersionSuccessDetails, {
                    versionName: selectedType?.name,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const handleVersionModal = (
        type: 'edit' | 'copy' | 'delete',
        version?: ReservationType,
        isOpen: boolean = false,
    ) => {
        setSelectedType(version);
        if (type === 'delete') {
            setIsDeleteModalOpen(isOpen);
        } else {
            setIsEditModalOpen(isOpen);
        }
    };

    const openDeleteModal = (version: ReservationType) => handleVersionModal('delete', version, true);
    const closeDeleteModal = () => handleVersionModal('delete', undefined);

    const openEditModal = (version: ReservationType) => handleVersionModal('edit', version, true);
    const closeEditModal = () => handleVersionModal('edit', undefined);

    const columns = fixedDurationReservationTypeTableColumns({
        openEditVersionModal: openEditModal,
        openDeleteVersionModal: openDeleteModal,
    });

    return (
        <ReservationVersionsProvider>
            <DataTable
                columns={columns}
                data={reservationData}
                refetch={refetch}
                search={search}
                sorting={sorting}
                pagination={pagination}
                isError={isError}
                isLoading={isLoading}
                semantics={{
                    title: formatMessage(reservationMessages.dynamicPlans),
                    entryName: formatMessage(reservationMessages.plan),
                    entryNames: formatMessage(reservationMessages.dynamicPlans),
                }}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                    newEntry: permissionReservationsSettings ? AddNewFixedDurationTypeModal : undefined,
                }}
                enableLocalFilters={true}
            />
            {permissionReservationsSettings && selectedType && (
                <>
                    {isDeleteModalOpen && (
                        <DeleteFixedDurationType
                            isDeleteVersionModalOpen={isDeleteModalOpen}
                            closeDeleteVersionModal={closeDeleteModal}
                            deleteVersion={deleteVersion}
                            selectedVersion={selectedType}
                        />
                    )}
                    {isEditModalOpen && (
                        <EditFixedDurationTypeModal
                            isModalOpen={isEditModalOpen}
                            closeEditModal={closeEditModal}
                            selectedType={selectedType}
                            refetch={refetch}
                        />
                    )}
                </>
            )}
        </ReservationVersionsProvider>
    );
};

export default FixedReservationTypes;
